// components/categories/StatsBar.tsx
'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { categoryStatsData, Stat } from '@/data/categories';
import { Users, UserCheck, Globe, ShieldCheck } from 'lucide-react';
import { CornerBracket } from './CornerBracket';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const ICON_MAP: Record<
  Stat['iconName'],
  React.FC<{ size: number; color: string; strokeWidth: number }>
> = {
  users: Users,
  worker: UserCheck,
  globe: Globe,
  badge: ShieldCheck,
};

export const StatsBar: React.FC = () => {
  const barRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const iconRefs = useRef<(HTMLDivElement | null)[]>([]);
  const numberRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useGSAP(
    () => {
      if (!barRef.current) return;

      const trigger = {
        trigger: barRef.current,
        start: 'top 88%',
        toggleActions: 'play none none reverse',
      };

      gsap.fromTo(
        itemRefs.current,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out', scrollTrigger: trigger }
      );

      gsap.fromTo(
        iconRefs.current,
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'back.out(2.2)', scrollTrigger: trigger }
      );

      categoryStatsData.forEach((stat, i) => {
        const el = numberRefs.current[i];
        if (!el) return;
        const obj = { val: 0 };
        gsap.to(obj, {
          val: stat.value,
          duration: 2,
          delay: i * 0.1,
          ease: 'power2.out',
          onUpdate() { el.textContent = Math.floor(obj.val).toLocaleString(); },
          scrollTrigger: trigger,
        });
      });
    },
    { scope: barRef }
  );

  return (
    <div
      ref={barRef}
      className="relative w-full grid grid-cols-2 lg:grid-cols-4 border-t border-b border-[#1C1B19]/[0.08] my-12 sm:my-16"
    >
      {/* Outer bar corner brackets — always visible */}
      {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
        <CornerBracket
          key={c}
          corner={c}
          size={14}
          thickness={1.5}
          color="#B08D57"
          style={{ opacity: 0.6 }}
        />
      ))}

      {categoryStatsData.map((stat, index) => {
        const Icon = ICON_MAP[stat.iconName];
        const isEven = (index + 1) % 2 === 0;
        const isLastLg = (index + 1) % 4 === 0;
        const isTopRow = index < 2;

        return (
          <div
            key={stat.id}
            ref={(el) => { itemRefs.current[index] = el; }}
            className={[
              'relative flex flex-col items-center justify-center gap-3 px-4 py-8',
              'border-r border-[#1C1B19]/[0.08]',
              isEven ? 'border-r-0 lg:border-r border-[#1C1B19]/[0.08]' : '',
              isLastLg ? 'lg:border-r-0' : '',
              isTopRow ? 'border-b border-[#1C1B19]/[0.08] lg:border-b-0' : '',
            ].join(' ')}
          >
            {/* Per-cell inner brackets — always visible */}
            <div className="absolute inset-[6px] pointer-events-none">
              {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
                <CornerBracket
                  key={c}
                  corner={c}
                  size={8}
                  thickness={1}
                  color="#B08D57"
                  style={{ opacity: 0.3 }}
                />
              ))}
            </div>

            {/* Icon */}
            <div
              ref={(el) => { iconRefs.current[index] = el; }}
              className="w-12 h-12 rounded-full border border-[#B08D57]/25 bg-gradient-to-br from-[#FAF6ED] to-[#EDE4CC] flex items-center justify-center shadow-[0_2px_8px_rgba(176,141,87,0.1)]"
            >
              <Icon size={19} color="#B08D57" strokeWidth={1.5} />
            </div>

            {/* Value */}
            <div className="text-center">
              <div className="flex items-baseline justify-center gap-0.5">
                <span
                  ref={(el) => { numberRefs.current[index] = el; }}
                  className="text-2xl xs:text-3xl font-extrabold text-[#1F1F1F] tabular-nums leading-none"
                >
                  0
                </span>
                <span className="text-xl font-extrabold text-[#B08D57] leading-none">
                  {stat.suffix}
                </span>
              </div>
              <span className="mt-1 block text-[10px] xs:text-[11px] tracking-[0.18em] uppercase text-[#9A9384] font-medium">
                {stat.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};